import { useState, useEffect, useContext } from 'react';
import { Grid } from '@mui/material';
import { format, endOfYear, getYear } from 'date-fns';
import { useTranslation } from 'react-i18next';
import constants from '../../../config/constants';
import {
    AppContext,
    SttDialog,
    SttFormCheck,
    SttFormPeriod,
    SttFormSelect,
    SttFormText,
    SttFormTextarea,
    SttSelectFixedInterval,
} from '../../../sporttia/all';
import { formatPriceForApi } from '../../../lib/utils';
import { PERMISSIONS } from '../../../lib/permissions';

type Group = {
    name?: string;
    maxMembers?: number;
    type?: string;
    idParent?: string | null;
    description?: string;
    ini?: string;
    end?: string;
    enrollmentPrice?: number;
    expiration?: string;
    feeTaxConceptCode?: string;
    signupTaxConceptCode?: string;
    feePrice?: number;
    feeInterval?: string;
    maxBeneficiaries?: number;
    chargeFirstFeeOnSignup?: boolean;
};

type ParentGroup = {
    id: string;
    name: string;
};

type Props = {
    open: boolean;
    onSave?: () => void;
    onClose?: () => void;
};

export default function CreateGroupDialog({
    open,
    onSave,
    onClose,
}: Props): React.ReactElement | null {
    const [group, setGroup] = useState<Group>();
    const [parentGroups, setParentGroups] = useState<ParentGroup[]>([]);

    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    /**
     * Load groups that they can be parents
     */
    function loadParentGroups() {
        cxt?.api('GET', `/scs/${cxt?.sc?.id}/groups`, {
            params: {
                year: getYear(new Date()),
                types: 'NORMAL,ABONADOS',
                page: 1,
                rows: 1000,
            },
            success: (result: { rows?: ParentGroup[] }) => {
                if (result.rows) {
                    setParentGroups(result.rows);
                }
            },
        });
    }

    useEffect(() => {
        if (open) loadParentGroups();
    }, [open]);

    useEffect(() => {
        // Set init group data
        if (open) {
            setGroup({
                name: '',
                maxMembers: 0,
                type: 'NORMAL',
                idParent: null,
                description: '',
                ini: format(new Date(), 'yyyy-MM-dd'),
                end: format(endOfYear(new Date()), 'yyyy-MM-dd'),
                enrollmentPrice: 0,
                expiration: '0000-00-00 00:00:00',
                feeTaxConceptCode: Object.values(constants.orgt.scIds).includes(
                    cxt?.sc?.id as 3444 | 2401,
                )
                    ? (constants.orgt.taxCodes.noOption ?? undefined)
                    : undefined,
                signupTaxConceptCode: Object.values(
                    constants.orgt.scIds,
                ).includes(cxt?.sc?.id as 3444 | 2401)
                    ? (constants.orgt.taxCodes.noOption ?? undefined)
                    : undefined,
            });
        }
    }, [open]);

    useEffect(() => {
        if (!group) return;

        if (group.type === 'NORMAL' || group.type === 'FAMILY') {
            // Remove fee fields for NORMAL and FAMILY types
            if ('feePrice' in group || 'feeInterval' in group) {
                const {
                    feePrice: _feePrice,
                    feeInterval: _feeInterval,
                    ...rest
                } = group;
                setGroup(rest);
            }
        } else if (group.type === 'ABONADOS') {
            // Add fee fields for ABONADOS type
            const needsFeePrice = !('feePrice' in group);
            const needsFeeInterval = !('feeInterval' in group);
            if (needsFeePrice || needsFeeInterval) {
                setGroup({
                    ...group,
                    ...(needsFeePrice && { feePrice: 0 }),
                    ...(needsFeeInterval && {
                        feeInterval: '0000-00-00 00:00:00',
                    }),
                });
            }
        }
    }, [group?.type]);

    function saveAndClose() {
        if (onSave && onSave.constructor === Function) {
            onSave();
        }
        if (onClose && onClose.constructor === Function) {
            onClose();
        }
    }

    /**
     * Create group
     */
    function save() {
        if (!group?.name?.trim()) {
            cxt?.showMessage('W', t('validation.nameRequired'));
            return;
        }

        // Validate that feeInterval is set when feePrice is greater than 0
        const feePrice = parseFloat(
            String(group?.feePrice ?? 0).replace(',', '.'),
        );
        const isFeeIntervalEmpty =
            !group?.feeInterval || group.feeInterval === '0000-00-00 00:00:00';

        if (feePrice > 0 && isFeeIntervalEmpty) {
            cxt?.showMessage('W', t('validation.feeIntervalRequired'));
            return;
        }

        if (group?.type === constants.group.types.family.name) {
            group.maxMembers = 0;
        }

        if (group?.feeInterval === '0000-00-00 00:00:00') {
            group.feeInterval = '';
        }

        const params = {
            ...group,
            ...(group.enrollmentPrice !== undefined && {
                enrollmentPrice: formatPriceForApi(group.enrollmentPrice),
            }),
            ...(group.feePrice !== undefined && {
                feePrice: formatPriceForApi(group.feePrice),
            }),
        };

        cxt?.api('POST', `/scs/${cxt?.sc?.id}/groups`, {
            params,
            success: (response: { group?: unknown }) => {
                if (response.group) {
                    cxt?.showMessage('S', t('Saved'));
                    saveAndClose();
                }
            },
        });
    }

    // Set named field to specified value
    function handleChange({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) {
        if (name) {
            setGroup({ ...group, [name]: value });
        }
    }

    if (!group) {
        return null;
    }

    return (
        <SttDialog
            title={t('NewGroup')}
            open={open}
            onClose={onClose}
            buttons={[
                {
                    type: 'save',
                    onClick: save,
                    permission: PERMISSIONS.GROUP.CREATE,
                },
            ]}
            content={
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 9 }}>
                        <SttFormText
                            name="name"
                            caption={t('Name')}
                            defVal={group.name}
                            onChange={handleChange}
                            required
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3 }}>
                        <SttFormSelect
                            name="type"
                            caption={t('Type')}
                            defVal={group.type}
                            options={[
                                {
                                    caption: t(
                                        constants.group.types.normal
                                            .translationCode,
                                    ),
                                    value: constants.group.types.normal.name,
                                },
                                {
                                    caption: t(
                                        constants.group.types.abonados
                                            .translationCode,
                                    ),
                                    value: constants.group.types.abonados.name,
                                },
                                {
                                    caption: t(
                                        constants.group.types.family
                                            .translationCode,
                                    ),
                                    value: constants.group.types.family.name,
                                },
                            ]}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        {group.type !== 'FAMILY' ? (
                            <SttFormText
                                name="maxMembers"
                                caption={t('MaxUsers')}
                                defVal={group.maxMembers?.toString()}
                                onChange={handleChange}
                            />
                        ) : (
                            <SttFormText
                                name="maxBeneficiaries"
                                caption={t('Beneficiaries')}
                                defVal={group.maxBeneficiaries?.toString()}
                                onChange={handleChange}
                            />
                        )}
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <SttFormText
                            name="enrollmentPrice"
                            caption={`${t('Inscription')} ${
                                cxt?.sc?.city?.country?.currencySymbol
                            }`}
                            defVal={group.enrollmentPrice?.toString()}
                            onChange={(price: {
                                name: string | undefined;
                                value: unknown;
                            }) => {
                                // Allow digits and comma as decimal separator
                                const sanitizedValue = (price.value as string)
                                    .replace('.', ',')
                                    .replace(/[^0-9,]/g, '');
                                handleChange({
                                    name: price.name,
                                    value: sanitizedValue,
                                });
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <SttFormSelect
                            name="idParent"
                            caption={t('ParentGroup')}
                            defVal={group.idParent}
                            options={parentGroups.map((currentGroup: any) => ({
                                caption: currentGroup.name,
                                value: currentGroup.id,
                            }))}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <SttFormTextarea
                            name="description"
                            caption={t('Description')}
                            defVal={group.description}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <SttFormPeriod
                            nameIni="ini"
                            nameEnd="end"
                            defValIni={group.ini}
                            defValEnd={group.end}
                            onChange={handleChange}
                        />
                    </Grid>
                    {(group.type === 'ABONADOS' || group.type === 'AF') && (
                        <>
                            <Grid size={{ xs: 12 }}>
                                <SttFormText
                                    name="feePrice"
                                    caption={t('feePrice')}
                                    defVal={group.feePrice?.toString()}
                                    onChange={(price: {
                                        name: string | undefined;
                                        value: unknown;
                                    }) => {
                                        // Allow digits and comma as decimal separator
                                        const sanitizedValue = (
                                            price.value as string
                                        )
                                            .replace('.', ',')
                                            .replace(/[^0-9,]/g, '');
                                        handleChange({
                                            name: price.name,
                                            value: sanitizedValue,
                                        });
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <SttSelectFixedInterval
                                    caption={t('generateFeesEvery')}
                                    name="feeInterval"
                                    defVal={
                                        group.feeInterval ||
                                        '0000-00-00 00:00:00'
                                    }
                                    options={[
                                        'NO PERIOD',
                                        '1 WEEK',
                                        '2 WEEK',
                                        '1 MONTH',
                                        '2 MONTH',
                                        '3 MONTH',
                                        '4 MONTH',
                                        '6 MONTH',
                                        '1 YEAR',
                                    ]}
                                    onChange={handleChange}
                                    required={
                                        parseFloat(
                                            String(group.feePrice ?? 0).replace(
                                                ',',
                                                '.',
                                            ),
                                        ) > 0
                                    }
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <SttFormCheck
                                    name="chargeFirstFeeOnSignup"
                                    caption={t(
                                        'page.sc.group.chargeFirstFeeOnSignup',
                                    )}
                                    checked={
                                        group.chargeFirstFeeOnSignup ?? false
                                    }
                                    onChange={handleChange}
                                />
                            </Grid>
                        </>
                    )}

                    <Grid size={{ xs: 12 }}>
                        <SttSelectFixedInterval
                            caption={t('Expiration')}
                            name="expiration"
                            defVal={group.expiration || '0000-00-00 00:00:00'}
                            options={['!FORTNIGHT']}
                            onChange={handleChange}
                        />
                    </Grid>
                    {Object.values(constants.orgt.scIds).includes(
                        cxt?.sc?.id as 3444 | 2401,
                    ) && (
                        <Grid size={{ md: 6, sm: 6, xs: 6 }}>
                            <SttFormSelect
                                name="feeTaxConceptCode"
                                caption={t('orgt.feeTaxConcept')}
                                defVal={
                                    group?.feeTaxConceptCode ??
                                    constants.orgt.taxCodes.noOption
                                }
                                options={[
                                    {
                                        caption: t('orgt.noOption'),
                                        value: null,
                                    },
                                    {
                                        caption: t('orgt.feePass'),
                                        value: constants.orgt.taxCodes
                                            .feePassCode,
                                    },
                                    {
                                        caption: t('orgt.feeRoomActivity'),
                                        value: constants.orgt.taxCodes
                                            .feeRoomActivity,
                                    },
                                    {
                                        caption: t('orgt.feeAquaticActivity'),
                                        value: constants.orgt.taxCodes
                                            .feeAquaticActivity,
                                    },
                                    {
                                        caption: t('orgt.athleticismFee'),
                                        value: constants.orgt.taxCodes
                                            .athleticismFee,
                                    },
                                    {
                                        caption: t('orgt.summerFees'),
                                        value: constants.orgt.taxCodes
                                            .summerFees,
                                    },
                                    {
                                        caption: t('orgt.facilityRental'),
                                        value: constants.orgt.taxCodes
                                            .facilityRental,
                                    },
                                ]}
                                onChange={handleChange}
                            />
                        </Grid>
                    )}
                    {Object.values(constants.orgt.scIds).includes(
                        cxt?.sc?.id as 3444 | 2401,
                    ) && (
                        <Grid size={{ md: 6, sm: 6, xs: 6 }}>
                            <SttFormSelect
                                name="signupTaxConceptCode"
                                caption={t('orgt.signupTaxConcept')}
                                defVal={
                                    group?.signupTaxConceptCode ??
                                    constants.orgt.taxCodes.noOption
                                }
                                options={[
                                    {
                                        caption: t('orgt.noOption'),
                                        value: null,
                                    },
                                    {
                                        caption: t('orgt.enrollment'),
                                        value: constants.orgt.taxCodes
                                            .enrollment,
                                    },
                                ]}
                                onChange={handleChange}
                            />
                        </Grid>
                    )}
                </Grid>
            }
        />
    );
}
